import { Component, OnInit, NgZone } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../core/auth/auth.service';
import { Router, ActivatedRoute } from '@angular/router';
import { environment } from '../../../environments/environment';

// Google Identity Services se carga vía script en index.html → objeto global `google`.
declare const google: any;

/**
 * La comprobación de sesión existente se hace UNA sola vez por carga de página.
 *
 * Si el interceptor no logra renovar la sesión, hace logout y navega a /login. Como
 * eso cambia la URL (se pierde el returnUrl), Angular recrearía el componente y una
 * comprobación por instancia entraría en bucle: 401 → refresh → logout → /login → 401…
 */
let sesionYaComprobada = false;

@Component({
  selector: 'app-login',
  standalone: false,
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  isLoading = false;
  errorMsg = '';
  currentYear = new Date().getFullYear();
  /** Habilita Google solo si el Client ID está realmente configurado. */
  googleEnabled = !!environment.googleClientId && !environment.googleClientId.startsWith('TU_');
  /** El formulario de correo está oculto por defecto (Google es la opción principal). */
  showEmailForm = false;
  /** Mientras se comprueba si ya hay sesión, no se enseñan los botones de acceso. */
  verificandoSesion = false;

  /** Cliente OAuth de Google (popup). Se inicializa una vez al cargar GIS. */
  private tokenClient: any = null;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private zone: NgZone,
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  ngOnInit() {
    this.comprobarSesionExistente();
    if (this.googleEnabled) this.initGoogle();
    // Si Google no está configurado, mostramos el correo directamente.
    if (!this.googleEnabled) this.showEmailForm = true;
  }

  /**
   * ¿El usuario ya tiene sesión válida? Entonces no hay nada que pedirle.
   *
   * A /login se puede llegar por un fallo pasajero del backend o simplemente
   * dejando la pestaña abierta. Como esta ruta no tiene guard, sin esta
   * comprobación la pestaña se quedaba mostrando "Inicia sesión para continuar"
   * indefinidamente aunque la cookie siguiera siendo perfectamente válida (y el
   * resto de pestañas funcionaran con normalidad).
   */
  private comprobarSesionExistente(): void {
    if (sesionYaComprobada) return;
    sesionYaComprobada = true;

    this.verificandoSesion = true;
    this.authService.loadCurrentUser().subscribe({
      next: () => {
        this.verificandoSesion = false;
        this.router.navigateByUrl(this.destino());
      },
      // Sin sesión (lo normal al entrar): se muestra el formulario, sin ruido.
      error: () => (this.verificandoSesion = false),
    });
  }

  /** A dónde ir tras entrar: lo que pidió el guard, o la bandeja. */
  private destino(): string {
    return this.route.snapshot.queryParams['returnUrl'] || '/';
  }

  /** Inicializa el cliente OAuth de Google. Reintenta hasta que el script GIS esté listo. */
  private initGoogle(attempt = 0) {
    if (typeof google === 'undefined' || !google.accounts?.oauth2) {
      if (attempt < 30) setTimeout(() => this.initGoogle(attempt + 1), 200);
      return;
    }
    this.tokenClient = google.accounts.oauth2.initTokenClient({
      client_id: environment.googleClientId,
      scope: 'openid email profile',
      callback: (resp: any) => this.zone.run(() => {
        if (resp?.access_token) {
          this.handleGoogleToken(resp.access_token);
        } else {
          this.errorMsg = 'No se pudo iniciar sesión con Google.';
          this.isLoading = false;
        }
      }),
      error_callback: () => this.zone.run(() => { this.isLoading = false; }),
    });
  }

  /** Acción del botón propio "Continuar con Google" → abre el popup de Google. */
  loginWithGoogle() {
    if (!this.tokenClient) {
      this.errorMsg = 'Google aún se está cargando, intenta de nuevo en un momento.';
      return;
    }
    this.isLoading = true;
    this.errorMsg = '';
    this.tokenClient.requestAccessToken();
  }

  private handleGoogleToken(accessToken: string) {
    this.authService.loginWithGoogle(accessToken).subscribe({
      next: () => {
        this.router.navigateByUrl(this.destino());
      },
      error: (err) => {
        this.errorMsg = err.error?.message || 'No se pudo iniciar sesión con Google.';
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  toggleEmailForm() {
    this.showEmailForm = !this.showEmailForm;
  }

  onSubmit() {
    if (this.loginForm.invalid) return;

    this.isLoading = true;
    this.errorMsg = '';

    this.authService.login(this.loginForm.value).subscribe({
      next: () => {
        this.router.navigateByUrl(this.destino());
      },
      error: (err) => {
        this.errorMsg = err.error?.message || 'Correo o contraseña incorrectos. Por favor, intenta de nuevo.';
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }
}
